import React from 'react';

import JoinSkipButton from '../join/JoinSkipButton';
import {
  CancelSkipButtonContainer,
  CancelSkipButtonSeparator,
  BottomWrapper,
  textLinkStyle,
} from './VerifyEmailWallStyles';
import VerifyEmailStep from './VerifyEmailStep';
import VerifyEmailOtpStep from './VerifyEmailOtpStep';
import VerifyEmailOtpSuccess from './VerifyEmailSuccess';
import { RootState } from '../../store';
import {
  resetOtpState,
  resetVerifyWallLoading,
  storeEmailVerifyWallData,
  verifyChangeNewEmail,
} from '../../actions/authActions';
import { useDispatch, useSelector } from 'react-redux';
import LoadingBackdrop from '../../atoms/loader/LoadingBackdrop';
import TextLink from '../common/TextLink';
import { DashlineSeparator } from '../common/CommonStyle';
import { useNavigate } from 'react-router-dom';

interface IVerifyEmailWallProps {
  handleCancel: () => void;
  onCompletionAction: () => void;
}

const VerifyEmailWall = ({ handleCancel, onCompletionAction }: IVerifyEmailWallProps): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { emailVerifyWallData, isVerifyEmailFetching } = useSelector((state: RootState) => state.auth);
  const account = useSelector((state: RootState) => state.profile);
  const hasUnVerifiedEmail =
    account?.email_validation_status &&
    account?.email_validation_status.has_valid === true &&
    account?.email_validation_status.has_verified === false;

  const [email, setEmail] = React.useState<string>(hasUnVerifiedEmail ? account?.email : '');
  const [step, setStep] = React.useState<string>(hasUnVerifiedEmail ? 'verify_otp' : 'verify_email');

  const resetState = (state: string): void => {
    dispatch(resetOtpState());
    dispatch(storeEmailVerifyWallData(null));
    dispatch(resetVerifyWallLoading());
    setStep(state);
  };

  React.useEffect(() => {
    if (hasUnVerifiedEmail && account?.email) {
      // dispatch(setVerifyWallLoading());
      dispatch(verifyChangeNewEmail(account?.email, 'verify_email_wall', true));
    }
  }, [account]);

  React.useEffect(() => {
    switch (emailVerifyWallData?.redirectTo) {
      case 'verify_otp':
        setStep('verify_otp');
        break;
      case 'verify_success':
        setStep('verify_success');
        break;
      case 'verify_email':
      default:
        setStep(hasUnVerifiedEmail ? 'verify_otp' : 'verify_email');
        break;
    }
  }, [emailVerifyWallData]);

  const renderStep = (): React.ReactElement => {
    switch (step) {
      case 'verify_otp':
        return (
          <VerifyEmailOtpStep
            email={emailVerifyWallData?.email ?? ''}
            setStep={setStep}
            data={emailVerifyWallData}
            hasUnVerifiedEmail={hasUnVerifiedEmail}
            setEmail={setEmail}
            isFetching={isVerifyEmailFetching}
          />
        );
      case 'verify_success':
        return (
          <VerifyEmailOtpSuccess
            email={emailVerifyWallData?.email ?? ''}
            handleCancel={(): void => {
              resetState('verify_email');
              onCompletionAction();
            }}
            data={emailVerifyWallData}
          />
        );
      case 'verify_email':
      default:
        return <VerifyEmailStep email={email} setEmail={setEmail} data={emailVerifyWallData} />;
    }
  };

  const handleBack = () => {
    setStep('verify_email');
  };

  const handleCloseModal = () => {
    resetState('verify_email');
    handleCancel();
  };

  return (
    <React.Fragment>
      {isVerifyEmailFetching && <LoadingBackdrop active={isVerifyEmailFetching} customStyle={{ height: '100%' }} />}
      {renderStep()}
      {step !== 'verify_success' && (
        <CancelSkipButtonContainer>
          {emailVerifyWallData && emailVerifyWallData.otpToken && !account?.email_validation_status.has_valid && (
            <React.Fragment>
              <JoinSkipButton label="Back" onClick={handleBack} customStyle={{ marginBottom: 20, color: '#8392A7' }} />
              <CancelSkipButtonSeparator>|</CancelSkipButtonSeparator>
            </React.Fragment>
          )}
          <JoinSkipButton
            label="Cancel"
            onClick={handleCloseModal}
            customStyle={{ marginBottom: 20, color: '#8392A7' }}
          />
        </CancelSkipButtonContainer>
      )}

      {['verify_otp'].includes(step) && (
        <>
          <DashlineSeparator />
          <BottomWrapper style={{ textAlign: 'center' }}>
            If you need to change your email, go to{' '}
            {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
            <TextLink
              onClick={() => {
                handleCloseModal();
                navigate('/settings/email-password');
              }}
              style={textLinkStyle}
            >
              Account Settings{' '}
            </TextLink>
          </BottomWrapper>
        </>
      )}
    </React.Fragment>
  );
};

export default VerifyEmailWall;
